import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import FileUpload from '../../components/FileUpload';
import { ArrowLeft, Plus, Trash2, Save, Eye, EyeOff, Leaf } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminEditFarmer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    api.get(`/admin/farmers/${id}`).then(r => {
      const f = r.data;
      setProfile({
        name: f.name || '',
        farm_name: f.farm_name || '',
        location: f.location || '',
        story: f.story || '',
        profile_image: f.profile_image || '',
        cover_image: f.cover_image || '',
        gallery: f.gallery || [],
        farming_practices: (f.farming_practices && f.farming_practices.length > 0) ? f.farming_practices : [''],
        lab_reports: f.lab_reports || [],
        intro_video: f.intro_video || '',
        internal_notes: f.internal_notes || '',
        status: f.status || 'draft',
      });
      setLoading(false);
    }).catch(() => { toast.error('Failed to load farmer'); setLoading(false); });
  }, [id]);

  const handleSave = async (publishing = false) => {
    if (!profile.name || !profile.farm_name || !profile.location) { toast.error('Name, farm name & location are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...profile,
        farming_practices: profile.farming_practices.filter(p => p && p.trim()),
        gallery: (profile.gallery || []).filter(Boolean),
        lab_reports: (profile.lab_reports || []).filter(r => r && r.url),
      };
      if (publishing) payload.status = 'published';
      await api.put(`/admin/farmers/${id}`, payload);
      toast.success(publishing ? 'Farmer published!' : 'Changes saved!');
      if (publishing) navigate('/admin/farmers');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading || !profile) return <div className="flex items-center justify-center h-full p-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const PreviewPanel = () => (
    <div className="bg-background rounded-xl border p-4 sticky top-24 max-h-[80vh] overflow-y-auto">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Customer View Preview</p>
      {profile.cover_image && <img src={profile.cover_image} alt="" className="w-full h-32 object-cover rounded-xl mb-3" />}
      <div className="flex items-center gap-3 mb-3">
        {profile.profile_image && <img src={profile.profile_image} alt="" className="w-12 h-12 rounded-xl object-cover" />}
        <div>
          <h4 className="font-serif font-medium text-sm">{profile.name || 'Farmer Name'}</h4>
          <p className="text-xs text-muted-foreground">{profile.farm_name || 'Farm Name'} {profile.location && `· ${profile.location}`}</p>
        </div>
      </div>
      {profile.farming_practices?.filter(p => p.trim()).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {profile.farming_practices.filter(p => p.trim()).map((p, i) => (
            <Badge key={i} variant="secondary" className="text-xs rounded-full"><Leaf className="h-2.5 w-2.5 mr-1" />{p}</Badge>
          ))}
        </div>
      )}
      {profile.story && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{profile.story}</p>}
      {profile.intro_video && <video src={profile.intro_video} controls className="w-full rounded-lg mt-2 max-h-40" />}
      {profile.gallery?.length > 0 && (
        <div className="grid grid-cols-3 gap-1 mt-3">
          {profile.gallery.slice(0, 6).map((url, i) => (
            <img key={i} src={url} alt="" className="aspect-square object-cover rounded-lg" />
          ))}
        </div>
      )}
      {profile.lab_reports?.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-xs font-semibold">Certifications</p>
          {profile.lab_reports.map((r, i) => <p key={i} className="text-xs text-primary">{r.name || `Report ${i+1}`}</p>)}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="admin-edit-farmer-page" className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-serif font-medium">Edit Farmer</h1>
          <p className="text-sm text-muted-foreground">{profile.name}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} className="rounded-full gap-2">
          {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} Preview
        </Button>
        <Button data-testid="save-edit-farmer-btn" onClick={() => handleSave(false)} className="rounded-full" disabled={saving}>
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className={`grid ${showPreview ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'} gap-6 max-w-7xl`}>
        <div className={showPreview ? 'lg:col-span-2' : ''}>
          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-lg">Farmer Profile</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Farmer Name *</Label><Input data-testid="edit-farmer-name" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} className="mt-1" /></div>
                <div><Label>Farm Name *</Label><Input data-testid="edit-farm-name" value={profile.farm_name} onChange={e => setProfile({...profile, farm_name: e.target.value})} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label>Location *</Label><Input data-testid="edit-farmer-location" value={profile.location} onChange={e => setProfile({...profile, location: e.target.value})} className="mt-1" /></div>
              </div>
              <div><Label>Story</Label><Textarea value={profile.story} onChange={e => setProfile({...profile, story: e.target.value})} className="mt-1" rows={5} /></div>
              <div><Label>Profile Image</Label><FileUpload folder="farmers/profile" onUpload={url => setProfile({...profile, profile_image: url})} existingFiles={profile.profile_image ? [profile.profile_image] : []} label="Upload profile photo" /></div>
              <div><Label>Cover Image</Label><FileUpload folder="farmers/cover" onUpload={url => setProfile({...profile, cover_image: url})} existingFiles={profile.cover_image ? [profile.cover_image] : []} label="Upload cover image" /></div>
              <div><Label>Farm Gallery (Photos & Videos)</Label><FileUpload folder="farmers/gallery" multiple onUpload={urls => setProfile({...profile, gallery: urls})} existingFiles={profile.gallery} accept="image/*,video/*" label="Upload photos and videos" /></div>
              <div><Label>Farmer Intro Video</Label><FileUpload folder="farmers/videos" type="video" onUpload={url => setProfile({...profile, intro_video: url})} existingFiles={profile.intro_video ? [profile.intro_video] : []} label="Upload intro video (MP4)" /></div>

              <div>
                <Label>Lab Reports & Certifications (Farm-level)</Label>
                <div className="space-y-2 mt-1">
                  {(profile.lab_reports || []).map((r, i) => (
                    <div key={i} className="flex gap-2 items-center p-2 bg-secondary/30 rounded-xl">
                      <Input placeholder="Report name (e.g. FSSAI License)" value={r.name || ''}
                        onChange={e => { const arr = [...profile.lab_reports]; arr[i] = { ...arr[i], name: e.target.value }; setProfile({ ...profile, lab_reports: arr }); }}
                        className="flex-1 text-sm" />
                      {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View</a>}
                      <Button variant="ghost" size="icon" onClick={() => setProfile({ ...profile, lab_reports: profile.lab_reports.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <FileUpload folder="farmers/reports" accept=".pdf,image/*"
                    onUpload={url => { if (!url) return; setProfile({ ...profile, lab_reports: [...(profile.lab_reports || []), { name: `Report ${(profile.lab_reports || []).length + 1}`, url }] }); }}
                    existingFiles={[]} label="Upload another report (PDF/image)" />
                </div>
              </div>

              <div>
                <Label>Farming Practices</Label>
                {profile.farming_practices.map((p, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <Input value={p} onChange={e => { const arr = [...profile.farming_practices]; arr[i] = e.target.value; setProfile({...profile, farming_practices: arr}); }} placeholder="e.g. Organic Farming" />
                    {profile.farming_practices.length > 1 && <Button variant="ghost" size="icon" onClick={() => setProfile({...profile, farming_practices: profile.farming_practices.filter((_, j) => j !== i)})}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setProfile({...profile, farming_practices: [...profile.farming_practices, '']})} className="mt-1"><Plus className="h-3 w-3 mr-1" /> Add Practice</Button>
              </div>

              <div><Label>Internal Notes</Label><Textarea value={profile.internal_notes} onChange={e => setProfile({...profile, internal_notes: e.target.value})} className="mt-1" rows={2} placeholder="Visible to admins only" /></div>

              <div className="flex gap-2 pt-4">
                <Button data-testid="save-farmer-btn" onClick={() => handleSave(false)} className="rounded-full" disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                {profile.status !== 'published' && (
                  <Button variant="outline" onClick={() => handleSave(true)} className="rounded-full" disabled={saving}>
                    Save & Publish
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        {showPreview && <div className="lg:col-span-1"><PreviewPanel /></div>}
      </div>
    </div>
  );
}
